import { useState, useEffect, useCallback } from "react";
import { useAnalytics } from "context/AnalyticsContext";
import { jobsService } from "services/jobsService";

const EMPTY_TRENDS = {
  totalJobs: 0,
  activeLocations: 0,
  functionBreakdown: [],
  seniorityBreakdown: [],
  workTypeBreakdown: [],
  locationBreakdown: [],
  hiringVelocity: [],
  topTitles: [],
  topSkills: [],
  functionGeoSignals: [],
};

const MARKET_TRENDS_ID = "market_trends";

export function useExpansionTrendsData() {
  const { selectedCompetitor, selectedPresetKey } = useAnalytics();

  const [trends, setTrends]                     = useState(EMPTY_TRENDS);
  const [intelligence, setIntelligence]         = useState(null);
  const [marketVelocity, setMarketVelocity]     = useState(null);

  const [loading, setLoading]                           = useState(false);
  const [intelligenceLoading, setIntelligenceLoading]   = useState(false);
  const [marketVelocityLoading, setMarketVelocityLoading] = useState(false);

  const [error, setError]                       = useState(null);
  const [intelligenceError, setIntelligenceError] = useState(null);

  const fetchCharts = useCallback(() => {
    if (!selectedCompetitor || !selectedPresetKey) {
      setTrends(EMPTY_TRENDS);
      return;
    }
    const preset = selectedPresetKey.toUpperCase();
    setLoading(true);
    setError(null);
    jobsService
      .getExpansionTrends(selectedCompetitor, preset)
      .then((res) => setTrends(res.data || EMPTY_TRENDS))
      .catch((err) => {
        console.error("Error fetching expansion trends:", err);
        setError("Failed to load hiring data");
        setTrends(EMPTY_TRENDS);
      })
      .finally(() => setLoading(false));
  }, [selectedCompetitor, selectedPresetKey]);

  const fetchInsights = useCallback(() => {
    if (!selectedCompetitor) {
      setIntelligence(null);
      return;
    }
    setIntelligenceLoading(true);
    setIntelligenceError(null);
    jobsService
      .getExpansionInsights(selectedCompetitor)
      .then((res) => setIntelligence(res.data || null))
      .catch((err) => {
        if (err.response?.status === 204) {
          setIntelligence(null);
        } else {
          console.error("Error fetching expansion insights:", err);
          setIntelligenceError("Could not load expansion insights");
        }
      })
      .finally(() => setIntelligenceLoading(false));
  }, [selectedCompetitor]);

  const fetchMarketVelocity = useCallback(() => {
    if (selectedCompetitor !== MARKET_TRENDS_ID || !selectedPresetKey) {
      setMarketVelocity(null);
      return;
    }
    const preset = selectedPresetKey.toUpperCase();
    setMarketVelocityLoading(true);
    jobsService
      .getMarketVelocity(preset)
      .then((res) => setMarketVelocity(res.data || null))
      .catch((err) => {
        console.error("Error fetching market velocity:", err);
        setMarketVelocity(null);
      })
      .finally(() => setMarketVelocityLoading(false));
  }, [selectedCompetitor, selectedPresetKey]);

  useEffect(() => { fetchCharts(); },        [fetchCharts]);
  useEffect(() => { fetchInsights(); },      [fetchInsights]);
  useEffect(() => { fetchMarketVelocity(); }, [fetchMarketVelocity]);

  return {
    trends,
    intelligence,
    marketVelocity,
    loading,
    intelligenceLoading,
    marketVelocityLoading,
    error,
    intelligenceError,
  };
}
